import React, { useMemo, useEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { graphql } from 'react-relay';
import { css } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Flex from '@atlaskit/primitives/components/flex';
import { xcss } from '@atlaskit/primitives/xcss';
import Text from '@atlaskit/primitives/text';
import Box from '@atlaskit/primitives/box';
import Heading from '@atlaskit/heading/heading';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { token } from '@atlaskit/tokens';

import type { Route } from '@confluence/route/entry-points/Route.ts';
import { Link } from '@confluence/route-manager/entry-points/Link.ts';
import { EDIT_PAGE, EDIT_BLOG } from '@confluence/named-routes/entry-points/routes.ts';
import { usePageState } from '@confluence/page-context';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop';

import type {
	TimeoutErrorScreenQuery$data,
	TimeoutErrorScreenQuery$variables,
} from './__generated__/TimeoutErrorScreenQuery.graphql.ts';

const timeoutErrorStyles = xcss({
	margin: 'auto',
	width: '50%',
	height: '90vh',
});

const timeoutErrorBorderContainerStyles = xcss({
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	padding: 'space.500',
	borderRadius: 'radius.large',
});

const headingStyles = xcss({
	paddingBottom: 'space.200',
});

const bulletStyles = css({
	paddingBottom: token('space.200'),
});

const i18n = defineMessages({
	heading: {
		id: 'content-prerequisites.timeout-error-screen.heading',
		defaultMessage: 'Unable to load page',
		description: 'Heading for unable to load page on timeout error screen',
	},
	resolutionOptions: {
		id: 'content-prerequisites.timeout-error-screen.resolution-options',
		defaultMessage: 'To resolve this issue, you may want to:',
		description: 'Text for resolution options on timeout error screen',
	},
	reloadPage: {
		id: 'content-prerequisites.timeout-error-screen.reload-page',
		defaultMessage: 'Reload this page',
		description: 'List item text for error message to reload the page',
	},
	convertToCloudEditor: {
		id: 'content-prerequisites.timeout-error-screen.convert-to-cloud-editor',
		defaultMessage:
			'<EditLink>Return to edit mode</EditLink> and convert to the <SACLink>cloud editor</SACLink> now',
		description:
			'List item text for error message and link to edit page and link to convert to the cloud editor',
	},
	removeMacroOrBreakPageContent: {
		id: 'content-prerequisites.timeout-error-screen.remove-excerpt-macro-or-break-page-content',
		defaultMessage:
			'Remove one or more excerpt macros, or break this page content into several smaller pages',
		description:
			'List item text for error message to remove the Inline Excerpt macro or break up page content',
	},
	contactAdmin: {
		id: 'content-prerequisites.timeout-error-screen.contact-admin',
		defaultMessage:
			'If this keeps happening, ask your admin to contact <SupportLink>our Support team</SupportLink> and give them the URL of this page.',
		description: 'Text for error message to contact admin to contact support team',
	},
});

export const TimeoutErrorScreen = ({
	contentId,
	spaceKey,
}: {
	contentId: any;
	spaceKey: any;
}): React.JSX.Element | null => {
	const [{ contentType }] = usePageState();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isBlogpost = contentType === 'blogpost';
	const routeType: Route = isBlogpost ? EDIT_BLOG : EDIT_PAGE;

	const { data } = useQueryInterop<TimeoutErrorScreenQuery$data, TimeoutErrorScreenQuery$variables>(
		graphql`
			query TimeoutErrorScreenQuery($contentId: ID!) {
				singleContent(id: $contentId) {
					id
					properties(key: "editor") {
						nodes {
							id
							key
							value
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId: contentId!,
			},
		},
	);
	const linkParams = useMemo<{
		contentId: string;
		spaceKey: string | undefined;
		contentType: string;
	}>(
		() => ({
			contentId,
			spaceKey,
			contentType: isBlogpost ? 'blog' : 'pages',
		}),
		[contentId, spaceKey, isBlogpost],
	);

	// If the editor property is undefined or null, we assume it's TinyMCE even though the missing missing property is not strictly TinyMCE.
	// It's okay to assume this for the purposes of this error screen b/c editor property as null is most likely TinyMCE.
	const editorProperty = data?.singleContent?.properties?.nodes?.[0]?.value;
	const isTinyMCE = !editorProperty || editorProperty === '"v1"';

	useEffect(() => {
		if (isTinyMCE) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'errorScreen',
					attributes: {
						customErrorUI: 'tinyMceTimeouts',
						editorProperty,
					},
				},
			})?.fire();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isTinyMCE, createAnalyticsEvent]);
	if (!isTinyMCE) return null;
	const editHref = routeType.toUrl(linkParams);

	return (
		<Flex xcss={timeoutErrorStyles} direction="column" justifyContent="center">
			<Box xcss={timeoutErrorBorderContainerStyles}>
				<Box xcss={headingStyles}>
					<Heading size="large" as="h2">
						<FormattedMessage {...i18n.heading} />
					</Heading>
				</Box>
				<Text as="p">
					<FormattedMessage {...i18n.resolutionOptions} />
				</Text>
				<ul css={bulletStyles}>
					<li>
						<FormattedMessage {...i18n.reloadPage} />
					</li>
					<li>
						<FormattedMessage
							{...i18n.convertToCloudEditor}
							values={{
								SACLink: (chunks: React.ReactNode[]) => (
									<Link
										target="_blank"
										href="https://support.atlassian.com/confluence-cloud/docs/convert-pages-to-the-new-editor"
									>
										{chunks}
									</Link>
								),
								EditLink: (chunks: React.ReactNode[]) => (
									<Link target="_blank" href={editHref}>
										{chunks}
									</Link>
								),
							}}
						/>
					</li>
					<li>
						<FormattedMessage {...i18n.removeMacroOrBreakPageContent} />
					</li>
				</ul>
				<Text as="p">
					<FormattedMessage
						{...i18n.contactAdmin}
						values={{
							SupportLink: (chunks: React.ReactNode[]) => (
								<Link target="_blank" href="https://support.atlassian.com/contact/#/">
									{chunks}
								</Link>
							),
						}}
					/>
				</Text>
			</Box>
		</Flex>
	);
};
